import _ from 'lodash'
import dayjs from 'dayjs'

import { Tag, baseApi } from './base'
import { getSocket } from './socket'

import { ConsultationOrder } from '@/core/models/consultation-order.model'
import { ExpertiseOrder } from '@/core/models/expertise-order.model'
import { Order, OrderStatus, UpdateOrderDto } from '@/core/models/order.model'
import { SearchRequestOptions } from '@/core/types/request'
import { SearchResponse } from '@/core/types/response'

import { cacher } from '@/utils/rtkQueryCacheUtils'
import { SocketEvent } from '@/core/types/socket-event'
import { MeetingLinkOrder } from '../models/meeting-link-order.model'
import { isMeetingLinkOrder } from '@/utils/type-guards'
import { WithId } from '../types/shared'

type SearchOrdersQuery = Record<string, any>

type GetOrderParams = {
  createdBy?: string
  status?: OrderStatus
}

type GetConsultationOrdersParams = GetOrderParams & {
  network?: string
  patient?: string
}

type GetExpertiseOrderParams = GetConsultationOrdersParams

type GetMeetingLinkOrdersParams = { acceptedBy?: string; excludedStatus?: OrderStatus[]; meetingLinks?: string[]; status?: OrderStatus[] }

type UpdateMeetingLinkOrderParams = WithId<
  UpdateOrderDto & {
    cancelReason?: string
    consultation?: string
    pds?: string
    refuseReason?: string
  }
>

// TODO: declare type
type UpdateConsultationOrderParams = Record<string, any>

export const orderApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrderByReferenceId: builder.query<Order, string>({
      query: (referenceId) => ({ url: `/orders/reference/${referenceId}`, method: 'GET' }),
    }),
    getOrders: builder.query<Order[], void>({
      query: () => ({ url: `/orders`, method: 'GET' }),
      providesTags: cacher.providesList(Tag.ORDER),
    }),
    getConsultationOrders: builder.query<ConsultationOrder[], GetConsultationOrdersParams>({
      query: (params) => ({ url: '/consultation-orders', method: 'GET', params }),
      async onCacheEntryAdded(_arg, { cacheDataLoaded, cacheEntryRemoved, updateCachedData }) {
        const socket = await getSocket()
        const consultationOrderCreatedListener = (consultationOrder: ConsultationOrder) => {
          updateCachedData((draftConsultationOrders) => {
            draftConsultationOrders.unshift(consultationOrder)
          })
        }
        try {
          await cacheDataLoaded
          socket.on(SocketEvent.CONSULTATION_ORDER_CREATED, consultationOrderCreatedListener)
        } catch {}
        await cacheEntryRemoved
        socket.off(SocketEvent.CONSULTATION_ORDER_CREATED, consultationOrderCreatedListener)
      },
    }),
    getExpertiseOrders: builder.query<ExpertiseOrder[], GetExpertiseOrderParams>({
      query: (params) => ({ url: '/expertise-orders', method: 'GET', params }),
      async onCacheEntryAdded(_arg, { cacheDataLoaded, cacheEntryRemoved, updateCachedData }) {
        const socket = await getSocket()
        const expertiseOrderCreatedListener = (expertiseOrder: ExpertiseOrder) => {
          updateCachedData((draftExpertiseOrders) => {
            draftExpertiseOrders.unshift(expertiseOrder)
          })
        }
        const expertiseOrderUpdatedListener = (updatedExpertiseOrder: ExpertiseOrder) => {
          updateCachedData((cachedExpertiseOrders) => {
            for (const cachedExpertiseOrder of cachedExpertiseOrders) {
              if (cachedExpertiseOrder._id !== updatedExpertiseOrder._id) continue
              Object.assign(cachedExpertiseOrder, updatedExpertiseOrder)
            }
          })
        }
        try {
          await cacheDataLoaded
          socket.on(SocketEvent.EXPERTISE_ORDER_CREATED, expertiseOrderCreatedListener)
          socket.on(SocketEvent.EXPERTISE_ORDER_UPDATED, expertiseOrderUpdatedListener)
        } catch {}
        await cacheEntryRemoved
        socket.off(SocketEvent.EXPERTISE_ORDER_CREATED, expertiseOrderCreatedListener)
        socket.off(SocketEvent.EXPERTISE_ORDER_UPDATED, expertiseOrderUpdatedListener)
      },
    }),
    searchOrders: builder.query<SearchResponse<Order>, { query: SearchOrdersQuery; options?: SearchRequestOptions }>({
      query: ({ query, options }) => ({
        url: '/orders/query',
        method: 'POST',
        body: { query, options: _.merge({}, { limit: 5, order: 'ASC', skip: 0, orderBy: 'startDate' }, options) },
      }),
      async onCacheEntryAdded({ query, options }, { cacheDataLoaded, cacheEntryRemoved, updateCachedData }) {
        const socket = await getSocket()
        const pendingOrderListener = (order: Order) => {
          if (query.status === OrderStatus.Pending) {
            updateCachedData((draft) => {
              draft.items.push(order)
              draft.items.sort((firstItem, secondItem) => {
                const sortOrder = options?.order === 'DESC' ? -1 : 1
                const firstItemOrderBy = _.get(firstItem, options?.orderBy ?? 'startDate')
                const secondItemOrderBy = _.get(secondItem, options?.orderBy ?? 'startDate')
                if (dayjs(firstItemOrderBy).isValid()) {
                  return (dayjs(firstItemOrderBy).toDate().getTime() - dayjs(secondItemOrderBy).toDate().getTime()) * sortOrder
                }

                return (firstItemOrderBy - secondItemOrderBy) * sortOrder
              })
              if (draft.items.length > (options?.limit ?? 5)) draft.items.splice(-1, 1)
              draft.totalCount = draft.totalCount + 1
            })
          }
        }
        try {
          await cacheDataLoaded
          socket.on(SocketEvent.PENDING_ORDER, pendingOrderListener)
        } catch {}
        await cacheEntryRemoved
        socket.off(SocketEvent.PENDING_ORDER, pendingOrderListener)
      },
      providesTags: (searchResponse) =>
        searchResponse
          ? [...searchResponse.items.map(({ _id }) => ({ type: Tag.ORDER, id: _id })), { type: Tag.ORDER, id: 'SEARCH' }]
          : [{ type: Tag.ORDER, id: 'SEARCH' }],
    }),
    updateOrder: builder.mutation<Order, WithId<UpdateOrderDto>>({
      query: ({ id: orderId, ...body }) => ({ url: `/orders/${orderId}`, method: 'PATCH', body }),
      invalidatesTags: (order) => {
        const invalidatesTags = [...cacher.invalidatesList(Tag.ORDER)(), { type: Tag.ORDER, id: 'SEARCH' }]
        if (order && isMeetingLinkOrder(order)) {
          invalidatesTags.push(...cacher.invalidatesList(Tag.MEETING_LINK_ORDER)(), { type: Tag.MEETING_LINK_ORDER, id: order._id })
        }
        return invalidatesTags
      },
    }),
    updateConsultationOrder: builder.mutation<ConsultationOrder, UpdateConsultationOrderParams>({
      query: ({ id: consultationOrderId, ...body }) => ({
        url: `/consultation-orders/${consultationOrderId}`,
        method: 'PATCH',
        body,
      }),
    }),
    acceptConsultationOrder: builder.mutation<ConsultationOrder, string>({
      query: (consultationOrderId) => ({ url: `/consultation-orders/${consultationOrderId}/accept`, method: 'PATCH' }),
      async onQueryStarted(consultationOrderId, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: updatedConsultationOrder } = await queryFulfilled
          const getConsultationOrdersArgs = orderApi.util.selectCachedArgsForQuery(getState(), 'getConsultationOrders')[0]
          dispatch(
            orderApi.util.updateQueryData('getConsultationOrders', getConsultationOrdersArgs, (draftConsultationOrders) => {
              for (const draftConsultationOrder of draftConsultationOrders) {
                if (draftConsultationOrder._id !== consultationOrderId) continue
                Object.assign(draftConsultationOrder, updatedConsultationOrder)
              }
            })
          )
        } catch {}
      },
    }),
    acceptExpertiseOrder: builder.mutation<ExpertiseOrder, string>({
      query: (expertiseOrderId) => ({ url: `/expertise-orders/${expertiseOrderId}/accept`, method: 'PATCH' }),
      async onQueryStarted(expertiseOrderId, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: updatedExpertiseOrder } = await queryFulfilled
          const getExpertiseOrdersArgs = orderApi.util.selectCachedArgsForQuery(getState(), 'getExpertiseOrders')[0]
          dispatch(
            orderApi.util.updateQueryData('getExpertiseOrders', getExpertiseOrdersArgs, (cachedExpertiseOrders) => {
              for (const cachedExpertiseOrder of cachedExpertiseOrders) {
                if (cachedExpertiseOrder._id !== expertiseOrderId) continue
                Object.assign(cachedExpertiseOrder, updatedExpertiseOrder)
              }
            })
          )
        } catch {}
      },
    }),
  }),
  overrideExisting: false,
})

export const meetingLinkOrderApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMeetingLinkOrders: builder.query<MeetingLinkOrder[], GetMeetingLinkOrdersParams>({
      query: (params) => ({ url: '/meeting-link-orders', method: 'GET', params }),
      providesTags: cacher.providesList(Tag.MEETING_LINK_ORDER),
    }),
    updateMeetingLinkOrder: builder.mutation<MeetingLinkOrder, UpdateMeetingLinkOrderParams>({
      query: ({ id: meetingLinkOrderId, ...body }) => ({ url: `/meeting-link-orders/${meetingLinkOrderId}`, method: 'PATCH', body }),
      invalidatesTags: (result, error, { id }) => [...cacher.invalidatesList(Tag.MEETING_LINK_ORDER)(), { type: Tag.MEETING_LINK_ORDER, id }],
    }),
    acceptMeetingLinkOrder: builder.mutation<MeetingLinkOrder, string>({
      query: (meetingLinkOrderId) => ({ url: `/meeting-link-orders/${meetingLinkOrderId}/accept`, method: 'PATCH' }),
      invalidatesTags: cacher.invalidatesList(Tag.MEETING_LINK_ORDER),
    }),
    refuseMeetingLinkOrder: builder.mutation<MeetingLinkOrder, string>({
      query: (meetingLinkOrderId) => ({ url: `/meeting-link-orders/${meetingLinkOrderId}/refuse`, method: 'PATCH' }),
      invalidatesTags: cacher.invalidatesList(Tag.MEETING_LINK_ORDER),
    }),
  }),
  overrideExisting: false,
})
